/**
 * Dual-handle fiscal year range slider.
 *
 * Outputs a comma-separated string of years (e.g., "2022,2023,2024")
 * compatible with the performance API's fiscal_year param.
 */
import { useState, useCallback, useRef, useEffect } from 'react'

interface Props {
  minFY?: number
  maxFY?: number
  value?: number | string
  onChange: (value: string) => void
}

function parseRange(value: number | string | undefined, minFY: number, maxFY: number): [number, number] {
  if (!value) return [minFY, maxFY]
  if (typeof value === 'number') return [value, value]
  const parts = String(value).split(',').map(Number).filter(Boolean).sort()
  if (parts.length === 0) return [minFY, maxFY]
  return [parts[0], parts[parts.length - 1]]
}

function yearsInRange(from: number, to: number): string {
  const years: number[] = []
  for (let y = from; y <= to; y++) years.push(y)
  return years.join(',')
}

export default function FYRangeSlider({ minFY = 2018, maxFY, value, onChange }: Props) {
  const now = new Date()
  const currentFY = now.getMonth() >= 9 ? now.getFullYear() + 1 : now.getFullYear()
  const max = maxFY ?? currentFY

  const [range, setRange] = useState<[number, number]>(() => parseRange(value, minFY, max))
  const rangeRef = useRef(range)
  const trackRef = useRef<HTMLDivElement>(null)

  // Keep ref in sync
  useEffect(() => { rangeRef.current = range }, [range])

  // Sync external value changes
  useEffect(() => {
    const [from, to] = parseRange(value, minFY, max)
    setRange([from, to])
  }, [value, minFY, max])

  const totalSteps = max - minFY || 1
  const leftPct = ((range[0] - minFY) / totalSteps) * 100
  const rightPct = ((range[1] - minFY) / totalSteps) * 100

  const updateRange = useCallback(
    (index: 0 | 1, newVal: number) => {
      const clamped = Math.max(minFY, Math.min(max, newVal))
      setRange((prev) => {
        const next: [number, number] = [...prev]
        next[index] = clamped
        if (next[0] > next[1]) {
          if (index === 0) next[1] = next[0]
          else next[0] = next[1]
        }
        return next
      })
    },
    [minFY, max],
  )

  const commit = useCallback(() => {
    // Read from ref to avoid stale closure
    const [from, to] = rangeRef.current
    onChange(yearsInRange(from, to))
  }, [onChange])

  const handlePointerDown = useCallback(
    (index: 0 | 1) => (e: React.PointerEvent) => {
      e.preventDefault()
      e.stopPropagation()
      const track = trackRef.current
      if (!track) return

      const onMove = (ev: PointerEvent) => {
        const rect = track.getBoundingClientRect()
        const pct = Math.max(0, Math.min(1, (ev.clientX - rect.left) / rect.width))
        const fy = Math.round(minFY + pct * totalSteps)
        updateRange(index, fy)
      }

      const onUp = () => {
        document.removeEventListener('pointermove', onMove)
        document.removeEventListener('pointerup', onUp)
        // Use requestAnimationFrame to ensure state has flushed
        requestAnimationFrame(commit)
      }

      document.addEventListener('pointermove', onMove)
      document.addEventListener('pointerup', onUp)
    },
    [minFY, totalSteps, updateRange, commit],
  )

  const handleTrackClick = useCallback(
    (e: React.MouseEvent) => {
      const track = trackRef.current
      if (!track) return
      const rect = track.getBoundingClientRect()
      const pct = (e.clientX - rect.left) / rect.width
      const fy = Math.round(minFY + pct * totalSteps)

      const distToLeft = Math.abs(fy - range[0])
      const distToRight = Math.abs(fy - range[1])
      const index: 0 | 1 = distToLeft <= distToRight ? 0 : 1

      const newRange: [number, number] = [...range]
      newRange[index] = fy
      if (newRange[0] > newRange[1]) {
        if (index === 0) newRange[1] = newRange[0]
        else newRange[0] = newRange[1]
      }
      setRange(newRange)
      // Commit immediately on click
      onChange(yearsInRange(newRange[0], newRange[1]))
    },
    [minFY, totalSteps, range, onChange],
  )

  // Generate tick marks
  const ticks: number[] = []
  for (let y = minFY; y <= max; y++) ticks.push(y)

  return (
    <div className="flex items-center gap-3">
      <span className="text-xs font-medium text-blue-600 dark:text-blue-400 whitespace-nowrap min-w-[80px] text-right">
        FY{range[0]}{range[0] !== range[1] ? `\u2013${range[1]}` : ''}
      </span>

      <div className="relative flex-1 h-8 flex items-center select-none">
        {/* Track background */}
        <div
          ref={trackRef}
          className="w-full h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full cursor-pointer relative"
          onClick={handleTrackClick}
        >
          {/* Active range highlight */}
          <div
            className="absolute h-full bg-blue-500 rounded-full pointer-events-none"
            style={{ left: `${leftPct}%`, width: `${rightPct - leftPct}%` }}
          />

          {/* Tick marks */}
          {ticks.map((fy) => {
            const pct = ((fy - minFY) / totalSteps) * 100
            return (
              <div
                key={fy}
                className="absolute top-3 -translate-x-1/2 pointer-events-none"
                style={{ left: `${pct}%` }}
              >
                <div className={`w-px h-1.5 mx-auto ${
                  fy >= range[0] && fy <= range[1] ? 'bg-blue-400' : 'bg-gray-300 dark:bg-gray-600'
                }`} />
                <span className={`text-[9px] block text-center mt-0.5 select-none ${
                  fy >= range[0] && fy <= range[1]
                    ? 'text-blue-600 dark:text-blue-400 font-medium'
                    : 'text-gray-400'
                }`}>
                  {fy}
                </span>
              </div>
            )
          })}
        </div>

        {/* Left handle */}
        <div
          className="absolute w-4 h-4 bg-white border-2 border-blue-500 rounded-full shadow cursor-grab active:cursor-grabbing -translate-x-1/2 hover:scale-110 transition-transform z-10"
          style={{ left: `${leftPct}%` }}
          onPointerDown={handlePointerDown(0)}
        />

        {/* Right handle */}
        <div
          className="absolute w-4 h-4 bg-white border-2 border-blue-500 rounded-full shadow cursor-grab active:cursor-grabbing -translate-x-1/2 hover:scale-110 transition-transform z-10"
          style={{ left: `${rightPct}%` }}
          onPointerDown={handlePointerDown(1)}
        />
      </div>
    </div>
  )
}
